import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { AdminAreaData, AdminAreaHierarchy } from "@/types/adminArea";
import { AdminAreaSettings } from "@/types/spatial";

import {
  getAdminAreaByIdApi,
  getAdminAreaHierarchy,
  getAllAdminAreaApi
} from "./adminAreaApi";

export type DataResponse = {
  id: string;
  name: string;
  province_id?: string;
};

export const ADMIN_AREA_KEYS = {
  lv0: ["lv0"] as const,
  lv1: ["lv1"] as const,
  lv2: ["lv2"] as const,
  lv3: ["lv3"] as const,
  lv4: ["lv3"] as const
} as const;

const getParentId = (level: number, address: AdminAreaSettings | null) => {
  switch (level) {
    case 1:
      return address?.lv1?.value;
    case 2:
      return address?.lv2?.value;
    case 3:
      return address?.lv3?.value;
    default:
      return address?.lv0?.value;
  }
};

const getQueryKey = (level: number, address: AdminAreaSettings | null) => {
  switch (level) {
    case 1:
      return [...ADMIN_AREA_KEYS.lv1, address?.lv1] as const;
    case 2:
      return [...ADMIN_AREA_KEYS.lv2, address?.lv2] as const;
    case 3:
      return [...ADMIN_AREA_KEYS.lv3, address?.lv3] as const;
    default:
      return [...ADMIN_AREA_KEYS.lv0, address?.lv0] as const;
  }
};

export const useAdmAreaQuery = (
  address: AdminAreaSettings | null,
  level: number,
  options?: Omit<
    Promise<unknown>[] | UseQueryOptions<AdminAreaData, Error, AdminAreaData>,
    "queryKey" | "queryFn"
  >
) => {
  const parentId = getParentId(level, address);

  return useQuery({
    queryKey: getQueryKey(level, address),
    queryFn: () => {
      if (!address?.lv0 || !parentId) return Promise.resolve(null);
      return getAllAdminAreaApi({
        country_id: address.lv0.value,
        parent_id: parentId,
        level,
        page: 1,
        per_page: 10
      });
    },
    enabled: !!address?.lv0 && !!getParentId(level, address),
    retry: 1,
    retryDelay: 1000,
    ...options
  });
};

export const useAdminAreaById = (
  id: string | null,
  zoomLevel?: number,
  options?: Omit<
    | Promise<unknown>[]
    | UseQueryOptions<AdminAreaData | null, Error, AdminAreaData | null>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AdminAreaData | null, Error> => {
  return useQuery({
    queryKey: ["admin-area-deatail", id, zoomLevel],
    queryFn: async () => {
      if (!id) return Promise.resolve(null);
      const response = await getAdminAreaByIdApi(id, zoomLevel);
      return {
        data: response ? [response] : []
      };
    },
    enabled: !!id,
    ...options
  });
};

export const useAdminAreaByIdMutation = () => {
  return useMutation({
    mutationFn: async ({
      id,
      zoomLevel
    }: {
      id: string;
      zoomLevel?: number;
    }) => {
      if (!id) return null;

      const response = await getAdminAreaByIdApi(id, zoomLevel);
      return {
        data: response ? [response] : []
      };
    }
  });
};

export const useGetAdminAreaHierarchy = (
  countryId: string,
  options?: Omit<
    UseQueryOptions<
      { data: AdminAreaHierarchy[] } | null,
      Error,
      { data: AdminAreaHierarchy[] } | null
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<{ data: AdminAreaHierarchy[] } | null, Error> => {
  return useQuery({
    queryKey: ["admin-area-hierarchy", countryId],
    queryFn: async () => {
      const response = await getAdminAreaHierarchy(countryId);
      return response ?? null;
    },
    enabled: !!countryId,
    ...options
  });
};
